import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { format, isThisMonth } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useForm } from 'react-hook-form';
import { FaAngleRight } from 'react-icons/fa';
import { HiDotsVertical } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  checkin,
  getCheckinPercentage,
} from '../../stores/slices/dashboard/dashboardActions';
import {
  assumeEmployeeRole,
  getEmployees,
} from '../../stores/slices/employees/employeesActions';
import Card from '../Card';
import * as yup from 'yup';
import { filterByValue } from '../../utils/filterByValue';

const MyTeam = () => {
  const { employees, isLoading } = useSelector(state => state.employees);
  const dispatch = useDispatch();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [isAssumming, setIsAssumming] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenAdjust,
    onOpen: onOpenAdjust,
    onClose: onCloseAdjust,
  } = useDisclosure();

  const searchRef = useRef();
  const cancelRef = useRef();
  const toast = useToast();
  const navigate = useNavigate();
  const schema = yup
    .object({
      due: yup.string().required('Due date is required'),
    })
    .required();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });
  useEffect(() => {
    dispatch(getEmployees());
    // eslint-disable-next-line
  }, []);
  const closeAlert = () => {
    setSelectedEmployee(null);
    setIsCheckingIn(false);
    onClose();
    onCloseAdjust();
  };
  const assumeRole = async id => {
    setIsAssumming(true);
    const res = await dispatch(assumeEmployeeRole({ id }));
    if (res.type === 'employees/assumeEmployeeRole/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      navigate('/dashboard/profile');
      toast({
        description: 'User assumed',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
    setIsAssumming(false);
    setSelectedEmployee(null);
  };
  const onSaveItem = async data => {
    const res = await dispatch(
      checkin({ id: selectedEmployee.next_check_in.id, adjustedDue: data.due })
    );
    if (res.type === 'dashboard/checkin/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      reset();
      dispatch(getEmployees());
      dispatch(getCheckinPercentage());
      toast({
        description: 'Checked in',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
    setSelectedEmployee(null);
    closeAlert();
  };
  const checkinUser = async () => {
    setIsCheckingIn(true);
    const res = await dispatch(
      checkin({ id: selectedEmployee.next_check_in.id, complete: true })
    );
    if (res.type === 'dashboard/checkin/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      dispatch(getEmployees());
      dispatch(getCheckinPercentage());
      toast({
        description: 'Checked in',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
    setIsCheckingIn(false);
    setSelectedEmployee(null);
    closeAlert();
  };
  const filter = data => {
    const text = data.target.value;
    setFilteredEmployees(filterByValue(employees, text));
  };

  const clearInput = () => {
    searchRef.current.value = '';
    setFilteredEmployees(employees);
  };
  useEffect(() => {
    setFilteredEmployees(employees);
  }, [employees]);
  return (
    <>
      <Box w={'full'} h={'full'}>
        <Card mx={'auto'} flex={'1'} flexGrow={'1'} h={'full'}>
          <VStack align={'start'} h={'full'}>
            <HStack
              direction={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
              position="relative"
              w={'full'}
              mb={'4'}
            >
              <Text fontWeight={'bold'} fontSize="md" w="full">
                My Team
              </Text>
              <Button
                color="primary"
                variant={'ghost'}
                size="xs"
                fontSize={'14px'}
                rightIcon={
                  <Icon
                    as={FaAngleRight}
                    border={'1px solid'}
                    borderRadius={'4'}
                  />
                }
                onClick={() => navigate('settings/organisation/employees')}
              >
                View All
              </Button>
            </HStack>
            <HStack w={'full'}>
              <InputGroup size="sm" maxW={'100%'}>
                <Input
                  pr="4.5rem"
                  placeholder="Search"
                  borderRadius={'4'}
                  onChange={filter}
                  ref={searchRef}
                />
                <InputRightElement width="4.5rem" justifyContent={'end'}>
                  <Button
                    size={'sm'}
                    borderLeftRadius={'none'}
                    onClick={clearInput}
                  >
                    Clear
                  </Button>
                </InputRightElement>
              </InputGroup>
            </HStack>
            <Box h={'full'} flex={1} w={'full'}>
              <Scrollbars>
                <Box overflow="hidden" flex={1} w={'full'}>
                  <TableContainer>
                    <Table
                      size={'sm'}
                      display={{
                        base: 'block',
                        md: 'table',
                      }}
                    >
                      <Thead>
                        <Tr>
                          <Th>Full name</Th>
                          <Th>Role</Th>
                          <Th>Last Check-In</Th>
                          <Th>Next Check-In</Th>
                          <Th></Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {employees &&
                          (isLoading ? (
                            <Tr>
                              <Td colSpan={'5'}>
                                <Spinner color="primary" />
                              </Td>
                            </Tr>
                          ) : filteredEmployees.length > 0 ? (
                            filteredEmployees.map(employee => (
                              <Tr key={employee.id} pr={'3'}>
                                <Td>{employee.full_name}</Td>
                                <Td>{employee.company_role}</Td>
                                <Td
                                  color={'lightGray.500'}
                                >
                                  {employee.last_checked_in_at
                                    ? format(
                                        new Date(employee.last_checked_in_at),
                                        'MMM do yyyy'
                                      )
                                    : '-'}
                                </Td>
                                <Td 
                                  // color={'lightGray.500'}
                                  color={() => {
                                    if (employee.next_check_in && employee.next_check_in.adjusted_due_at) {
                                      if (new Date(employee.next_check_in.adjusted_due_at) > new Date()) {
                                        return 'primary';
                                      } else {
                                        return 'red.400';
                                      }
                                    }
                                  }}
                                >
                                  {employee.next_check_in
                                    ? format(
                                        new Date(
                                          employee.next_check_in.adjusted_due_at
                                        ),
                                        'MMM do yyyy'
                                      )
                                    : '-'}
                                </Td>
                                <Td align={'right'} textAlign={'right'}>
                                  <HStack justify={'end'}>
                                    <Menu>
                                      <Tooltip label="Options">
                                        <MenuButton
                                          as={IconButton}
                                          aria-label="Options"
                                          variant={'outline'}
                                          size="xs"
                                          border={'1.5px solid'}
                                          borderColor={'lightGray.500'}
                                          p={0}
                                          color="lightGray.500"
                                          _hover={{
                                            color: 'primary',
                                            borderColor: 'primary',
                                          }}
                                          icon={<HiDotsVertical />}
                                          isLoading={
                                            (isAssumming &&
                                              selectedEmployee ===
                                                employee.id) ||
                                            (isCheckingIn &&
                                              selectedEmployee.id ===
                                                employee.id)
                                          }
                                        />
                                      </Tooltip>
                                      <MenuList>
                                        <MenuItem
                                          onClick={() => {
                                            setSelectedEmployee(employee.id);
                                            assumeRole(employee.id);
                                          }}
                                        >
                                          Assume Role
                                        </MenuItem>
                                        <MenuItem
                                          isDisabled={!employee.next_check_in}
                                          onClick={() => {
                                            setSelectedEmployee(employee);
                                            onOpen();
                                          }}
                                        >
                                          Check-in
                                        </MenuItem>
                                        <MenuItem
                                          isDisabled={!employee.next_check_in}
                                          onClick={() => {
                                            setSelectedEmployee(employee);
                                            onOpenAdjust();
                                          }}
                                        >
                                          Adjust due date
                                        </MenuItem>
                                      </MenuList>
                                    </Menu>
                                  </HStack>
                                </Td>
                              </Tr>
                            ))
                          ) : (
                            <Tr>
                              <Td colSpan={'5'}>No team members found</Td>
                            </Tr>
                          ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              </Scrollbars>
            </Box>
          </VStack>
        </Card>
      </Box>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeAlert}
        blockScrollOnMount={false}
        closeOnOverlayClick={isCheckingIn ? false : true}
        closeOnEsc={isCheckingIn ? false : true}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Check-in user?
            </AlertDialogHeader>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant={'outline'}
                onClick={closeAlert}
                disabled={isCheckingIn}
              >
                No
              </Button>
              <Button
                bg={'primary'}
                _hover={{
                  color: 'primary',
                  bg: 'transparent',
                }}
                onClick={() => checkinUser()}
                ml={3}
                isLoading={isCheckingIn}
              >
                Yes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <AlertDialog
        isOpen={isOpenAdjust}
        leastDestructiveRef={cancelRef}
        onClose={closeAlert}
        blockScrollOnMount={false}
        closeOnOverlayClick={isSubmitting ? false : true}
        closeOnEsc={isSubmitting ? false : true}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <form onSubmit={handleSubmit(onSaveItem)}>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Adjust due date
              </AlertDialogHeader>
              <AlertDialogBody>
                <FormControl id="due" isInvalid={errors.due}>
                  <FormLabel>Due date</FormLabel>
                  <Input type={'date'} id="due" {...register('due')} />
                  <FormErrorMessage>
                    {errors.due && errors.due.message}
                  </FormErrorMessage>
                </FormControl>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button
                  ref={cancelRef}
                  variant={'outline'}
                  onClick={closeAlert}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  bg={'primary'}
                  _hover={{
                    color: 'primary',
                    bg: 'transparent',
                  }}
                  type="submit"
                  ml={3}
                  isLoading={isSubmitting}
                >
                  Save
                </Button>
              </AlertDialogFooter>
            </form>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default MyTeam;
